import isEqual from 'lodash/isEqual';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useBorderControl} from '~/common/canvas/hooks/useBorderControl';
import {useCanvas} from '~/common/canvas/hooks/useCanvas';
import {useCanvasEvents} from '~/common/canvas/hooks/useCanvasEvents';
import {useOrientation} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOrientation';
import {useOutsideAlerter} from '~/common/FabricInvitation/EventCreateDesign/hooks/useOutsideAlerter';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {CANVAS_ORIENTATION} from './constants';
import {initAligningGuidelines} from './controls/align_lines';
import {selectFirstTextbox} from './utils/textboxUtils';
import {selectFirstUyoImage} from './utils/uyoUtils';

export const Canvas = ({
  configJson,
  onChange,
  setCanvas: setCanvasElem,
  setActiveObjects,
  onLoad,
  saveSnapshot,
  className = '',
  parentNode = '',
  readOnly = false,
  callback,
}) => {
  const isDesktop = useIsLargishAndUp();
  const orientation = useOrientation();
  const device = isDesktop ? 'desktop' : 'mobile';
  const canvasEl = useRef(null);
  const canvasRef = useRef();
  const canvasWrapperRef = useRef(null);
  const onChangeRef = useRef();
  const [groupCount, setGroupCount] = useState(0);
  const {handleBorderLimits} = useBorderControl();
  onChangeRef.current = onChange;

  const onChangeHandler = useCallback(() => {
    if (groupCount > 0) return;
    const objectTextFilter = (object) => object.type === 'textbox';
    if (!canvasRef.current?.isEmpty()) {
      const fabricJson = canvasRef.current.toJSON([
        'id',
        '_originalObject',
        'letterCasing',
        '_originalText',
      ]);
      if (isEqual(fabricJson, configJson)) return;
      onChangeRef.current({...fabricJson}, configJson.objects.filter(objectTextFilter).length);
    }
  }, [groupCount, configJson]);

  const incrGroupCount = useCallback(() => {
    setGroupCount((prev) => prev + 1);
  }, []);

  const decrGroupCount = useCallback(() => {
    setGroupCount((prev) => prev - 1);
    if (groupCount === 1) {
      onChangeHandler();
    }
  }, [groupCount, onChangeHandler]);

  const onSelectionEvent = useCallback(
    (e) => {
      setActiveObjects(e.selected);
    },
    [setActiveObjects]
  );

  const onDeselectionEvent = useCallback(() => {
    setActiveObjects(null);
    if (callback && typeof callback === 'function') {
      callback();
    }
  }, [setActiveObjects, callback]);

  const onUyoSelectFirst = useCallback(() => {
    const success = selectFirstUyoImage(canvasRef.current);
    if (success) {
      setActiveObjects(canvasRef.current?.getActiveObjects());
    }
  }, [setActiveObjects]);

  const onTextboxSelectFirst = useCallback(() => {
    const success = selectFirstTextbox(canvasRef.current);
    if (success) {
      setActiveObjects(canvasRef.current?.getActiveObjects());
    }
  }, [setActiveObjects]);

  const getOrientation = CANVAS_ORIENTATION[device][orientation];

  const onClickOutsideCanvas = () => {
    const canvasInstance = canvasRef.current;
    if (canvasInstance) {
      const activeObject = canvasInstance?.getActiveObject();
      activeObject?.exitEditing?.();
      canvasInstance?.renderAll();
    }
  };

  useOutsideAlerter(canvasWrapperRef, onClickOutsideCanvas);

  const saveCanvas = (canvasInstance) => {
    setCanvasElem(canvasInstance);
    canvasRef.current = canvasInstance;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore dependencies
  useEffect(() => {
    let canvasInstance = {};
    // biome-ignore lint/suspicious/noEmptyBlockStatements: empty block
    let setCurrentDimensions = () => {};
    const handleResize = () => {
      if (canvasWrapperRef.current && canvasWrapperRef.current.offsetWidth > 0) {
        requestAnimationFrame(() => {
          setCurrentDimensions(parentNode, canvasWrapperRef);
        });
      }
    };

    window.addEventListener('resize', handleResize);

    const c = useCanvas(
      canvasEl.current,
      configJson,
      (canvas) => {
        saveCanvas(canvas);
        onLoad(canvas);
        handleResize();
      },
      isDesktop,
      {orientation, dimensions: getOrientation},
      680,
      readOnly
    );
    canvasInstance = c.canvasInstance;
    setCurrentDimensions = c.setCurrentDimensions;
    canvasEl.current.fabric = canvasInstance;

    initAligningGuidelines(canvasInstance);

    // used for automatic tests
    window.canvasElementSelector = (index = 1) => {
      const textboxes = canvasInstance.getObjects().filter((obj) => obj.type === 'textbox');
      if (textboxes.length > 0) {
        canvasInstance.setActiveObject(textboxes[textboxes.length - index]);
        canvasInstance.renderAll();
      }
      return textboxes.length;
    };

    return () => {
      if (saveSnapshot) {
        const snapshot = canvasInstance.toDataURL({
          enableRetinaScaling: true,
        });
        saveSnapshot(snapshot);
      }

      canvasInstance.dispose();
      window.removeEventListener('resize', handleResize);
    };
  }, [readOnly, orientation]);

  useCanvasEvents({
    canvasInstance: canvasRef.current,
    onChangeHandler,
    onSelectionEvent,
    onDeselectionEvent,
    handleBorderLimits,
    incrGroupCount,
    decrGroupCount,
    onUyoSelectFirst,
    onTextboxSelectFirst,
  });

  return (
    <div ref={canvasWrapperRef} className={className}>
      <canvas id="fabric-canvas" ref={canvasEl} style={readOnly ? {pointerEvents: 'none'} : null} />
    </div>
  );
};
